import React from "react";
import { RedactionResult } from "../types/redaction.types";
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { useRedactionTool } from "../hooks/useRedactionTool";
import ModelSelector from "./common/ModelSelector";
import RedactionTypeSelector from "./common/RedactionTypeSelector";
import TrustToleranceSettings from "./common/TrustToleranceSettings";
import { redactionModels } from "../utils/helpers";
// Removed unused recharts imports and PIE_COLORS constant

// Check environment
const isDev = true;

interface RedactionToolProps {
  onRedactionComplete: (result: RedactionResult) => void;
}

const RedactionTool: React.FC<RedactionToolProps> = ({
  onRedactionComplete,
}) => {
  const {
    inputFile,
    modelChoice,
    redactionType,
    toleranceSettings,
    isLoading,
    error,
    successMessage,
    handleFileChange,
    handleModelChange,
    handleRedactionTypeChange,
    handleToleranceSettingsChange,
    handleRedactClick,
  } = useRedactionTool(onRedactionComplete);

  return (
    <Container className="mt-3">
      <Row className="justify-content-center">
        <Col>
          <Card>
            <Card.Header as="h2">AI Redaction Tool</Card.Header>
            <Card.Body className="py-4">
              {/* Conditional Alert Description */}
              {isDev ? (
                <Alert variant="light" className="mb-4">
                  Upload a{" "}
                  <strong>CSV, Excel (.xlsx), PDF, or Word (.docx) file</strong>
                  . The selected NER model will identify names within the file's
                  text content, replace them with <strong>***</strong>, and
                  provide the modified file for download in the original format.
                </Alert>
              ) : (
                <Alert variant="light" className="mb-4">
                  Upload a <strong>CSV or Excel (.xlsx) file</strong>. The AI
                  will identify names within the file's text content using the
                  default AI model, replace them with <strong>***</strong>, and
                  provide the modified file for download in the original format.
                </Alert>
              )}

              <Form onSubmit={handleRedactClick}>
                <Form.Group controlId="fileUpload" className="mb-4">
                  {/* Conditional Label */}
                  <Form.Label>
                    {isDev ? "1. " : ""}Upload File (CSV, XLSX, PDF, or DOCX)
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".csv,.xlsx,.pdf,.docx,.doc,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                    onChange={handleFileChange}
                    required
                  />
                </Form.Group>

                {/* Trust-Based Tolerance Settings */}
                <TrustToleranceSettings
                  settings={toleranceSettings}
                  onSettingsChange={handleToleranceSettingsChange}
                />

                {/* Conditional Model Selector */}
                {isDev && (
                  <>
                    <ModelSelector
                      label="2. Select NER Model"
                      value={modelChoice}
                      onChange={handleModelChange}
                      models={redactionModels}
                      controlId="redactionModelSelect"
                      className="mb-2"
                    />
                    <Form.Text muted className="d-block mb-4">
                      The first time a model is selected, it may take a minute
                      to load on the server.
                    </Form.Text>
                  </>
                )}

                {/* Redaction Type Selector */}
                <RedactionTypeSelector
                  label={
                    isDev
                      ? "3. Select Redaction Type"
                      : "2. Select Redaction Type"
                  }
                  value={redactionType}
                  onChange={handleRedactionTypeChange}
                  className="mb-4"
                />

                <Button
                  variant="primary"
                  type="submit"
                  disabled={isLoading || !inputFile}
                  className="mt-2 w-100"
                >
                  {isLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Processing...
                    </>
                  ) : (
                    "Identify Names and Download Redacted File"
                  )}
                </Button>
              </Form>

              {error && (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              )}
              {successMessage && (
                <Alert variant="success" className="mt-3">
                  {successMessage}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RedactionTool;
